/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from "react";
import { ResizeGridProps } from "./dto";
import { isLineInCell } from "../../utils";
import { cn } from "../../../utils";
import { useLatest } from "ahooks";

const getTotal = (list: number[], idx: number, defaultValue = 0) =>
  list.slice(0, idx + 1).reduce((pre, cur) => pre + (cur ?? defaultValue), 0);

interface Resize {
  type?: "col" | "row";
  index?: number;
  x?: number;
  y?: number;
}
const ResizeGrid = ({
  enable: outsideEnable = true,
  cols,
  rows,
  onResizeStart,
  onResizeEnd,
  onChange,
}: ResizeGridProps) => {
  const [resize, setResize] = useState<Resize>({});
  const resizeRef = useLatest(resize);
  const [enable, setEnable] = useState<Pick<Resize, "type" | "index">>({});

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      const { type, index, x, y } = resizeRef.current ?? {};
      if (!type) return;
      if (type === "col") {
        onChange?.({ type, index, offset: e.clientX - x });
      }
      if (type === "row") {
        onChange?.({ type, index, offset: e.clientY - y });
      }
      setResize({
        type,
        index,
        x: e.clientX,
        y: e.clientY,
      });
    };
    const onMouseUp = () => {
      onResizeEnd?.();
      setResize({});
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    };
  }, []);

  return (
    <div className="wbuc-pointer-events-none wbuc-absolute wbuc-inset-0 wbuc-size-full">
      {cols.map((i, idx) => (
        <div
          key={idx}
          className={cn(
            "wbuc-absolute wbuc-h-full wbuc-w-[2px] [pointer-events:all]",
            {
              "wbuc-cursor-col-resize hover:wbuc-bg-[#3B65EE]":
                outsideEnable && enable.type === "col" && enable.index === idx,
              "wbuc-cursor-col-resize wbuc-bg-[#3B65EE]":
                resize.type === "col" && resize.index === idx,
            }
          )}
          style={{
            transform: `translateX(${getTotal(cols, idx)}px)`,
          }}
          onMouseDown={(e) => {
            if (e.nativeEvent.button !== 0 || !enable.type || !outsideEnable)
              return;
            const newResize: Resize = {
              type: "col",
              index: idx,
              x: e.clientX,
            };
            setResize(newResize);
            onResizeStart?.();
          }}
          onMouseEnter={(e) => {
            const inCell = isLineInCell(e, "col");
            if (!inCell) {
              setEnable({ type: "col", index: idx });
            }
          }}
          onMouseLeave={() => {
            setEnable({});
          }}
        ></div>
      ))}
      {rows.map((i, idx) => (
        <div
          key={idx}
          className={cn(
            "wbuc-absolute wbuc-h-[2px] wbuc-w-full [pointer-events:all]",
            {
              "wbuc-cursor-row-resize hover:wbuc-bg-[#3B65EE]":
                outsideEnable && enable.type === "row" && enable.index === idx,
              "wbuc-cursor-row-resize wbuc-bg-[#3B65EE]":
                resize.type === "row" && resize.index === idx,
            }
          )}
          style={{
            transform: `translateY(${getTotal(rows, idx)}px)`,
          }}
          onMouseDown={(e) => {
            if (e.nativeEvent.button !== 0 || !enable.type || !outsideEnable)
              return;
            const newResize: Resize = {
              type: "row",
              index: idx,
              y: e.clientY,
            };
            setResize(newResize);
            onResizeStart?.();
          }}
          onMouseEnter={(e) => {
            const inCell = isLineInCell(e, "row");
            if (!inCell) {
              setEnable({ type: "row", index: idx });
            }
          }}
          onMouseLeave={() => {
            setEnable({});
          }}
        ></div>
      ))}
    </div>
  );
};

export default ResizeGrid;
